import cloneDeep from 'lodash/cloneDeep';
import type { App } from '../../app/App';
import { GameSceneMVC } from '../scene/GameSceneMVC';
import { Solver } from './Solver';
import type { SolverResults } from './types';
import { SolverHistoryView } from './SolverHistoryView';
import { StrategyPrioritizeColors } from './strategies/StrategyPrioritizeColors';
import type { LevelData } from '../level/LevelData';
import { getLevelData, RAW_LEVELS } from '../../../levels';
import { SolverLiveView } from './SolverLiveView';
import MathUtils from '../../utils/MathUtils';
import { StrategyPrioritizeV2 } from './strategies/StrategyPrioritizeV2';
import { StrategyPrioritizeV3 } from './strategies/StrategyPrioritizeV3';

const solverSettings = {
  fromLevel: 0,
  toLevel: Infinity,
};

const solvers = {
  prioritize: () => new StrategyPrioritizeColors(),
  prioritizeV2: () => new StrategyPrioritizeV2(),
  prioritizeV3: () => new StrategyPrioritizeV3(),
  // outcome: () => new StrategyOutcome(),
} as const;

let currentSolver: keyof typeof solvers = 'prioritize';

interface SolveGameResults {
  label: string;
  won: number;
  lost: number;
  averageWinMoves: number;
  winRate: number;
  time: number;
}

export function solveGame(
  solver: keyof typeof solvers,
  levelData: LevelData,
  iterations: number,
  label?: string,
): SolveGameResults {
  const startTime = Date.now();
  const results: SolverResults[] = [];
  for (let i = 0; i < iterations; i += 1) {
    results.push(new Solver(solvers[solver](), cloneDeep(levelData), label).start());
  }

  const won = results.filter((r) => r.resolved);
  const lost = results.filter((r) => !r.resolved);

  return {
    label: label ?? solver,
    won: won.length,
    lost: lost.length,
    averageWinMoves: MathUtils.getAverage(won.map((run) => run.movesMade)),
    time: Date.now() - startTime,
    winRate: won.length / iterations,
  };
}

function compareSolvers(levelData: LevelData, label?: string): Record<string, SolveGameResults> {
  const result = Object.fromEntries(
    Object.keys(solvers).map((key) => [key, solveGame(key as keyof typeof solvers, cloneDeep(levelData), 100, label)]),
  );

  console.log(result);

  return result;
}

export function getSolverDebugMenuFunctions(app: App): Record<string, () => void> {
  const {
    controller: { sceneManager },
  } = app;

  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Test solver live': (): void => {
      if (!(sceneManager.activeScene instanceof GameSceneMVC)) return;

      const liveView = new SolverLiveView(
        new Solver(solvers[currentSolver](), cloneDeep(sceneManager.activeScene.options.levelData), ''),
      );
      liveView.props({
        parent: liveView.pixi.stage,
      });
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Test all levels': (): void => {
      const results = RAW_LEVELS.map((_, i) => {
        if (i < solverSettings.fromLevel || i > solverSettings.toLevel) return null;
        return compareSolvers(getLevelData(i), `Level ${i + 1}`);
      });

      const report = results.map((levelResult, index) => {
        if (!levelResult) return '---';

        const bestSolver = Object.entries(levelResult).reduce((prev, cur) => (cur[1].won > prev[1].won ? cur : prev));

        return `Level ${index + 1}, best: ${bestSolver[0]}, win rate: ${bestSolver[1].winRate}`;
      });

      console.log({ results, report });
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Compare solvers all levels': (): void => {
      RAW_LEVELS.forEach((_, i) => {
        console.log(`Level ${i}`);
        compareSolvers(getLevelData(i));
      });
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Compare solvers': (): void => {
      const { activeScene } = sceneManager;
      if (!(activeScene instanceof GameSceneMVC)) return;
      compareSolvers(activeScene.options.levelData);
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Set prioritize strategy': (): void => {
      currentSolver = 'prioritize';
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Set prioritize v2': (): void => {
      currentSolver = 'prioritizeV2';
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Test current level': (): void => {
      if (sceneManager.activeScene instanceof GameSceneMVC) {
        new Solver(solvers[currentSolver](), sceneManager.activeScene.options.levelData).start();
      }
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Solver visualisation': (): void => {
      if (!(sceneManager.activeScene instanceof GameSceneMVC)) return;

      const solver = new Solver(solvers[currentSolver](), sceneManager.activeScene.options.levelData);
      solver.start();

      const solverView = new SolverHistoryView(solver.getHistory());
      solverView.props({
        parent: solverView.pixi.stage,
      });
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Test current level x100': (): void => {
      if (!(sceneManager.activeScene instanceof GameSceneMVC)) return;
      console.log(solveGame(currentSolver, sceneManager.activeScene.options.levelData, 100));
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Test current level x1000': (): void => {
      if (!(sceneManager.activeScene instanceof GameSceneMVC)) return;
      console.log(solveGame(currentSolver, sceneManager.activeScene.options.levelData, 1000));
    },
  };
}
